import { ComponentType, createContext, useContext, useMemo } from "react";
import { Toaster } from 'react-hot-toast';
import camelCase from 'lodash.camelcase';

import DefaultLoader, { LoaderComponentType } from "../components/Loader";
import DefaultImageLoader from '../assets/image-loader.svg';
import UserDropdown from '../theme/core/layouts/components/navbar/UserDropdown';
import AlertsDropdown from '../theme/core/layouts/components/navbar/AlertsDropdown';

const DefaultUserDropdown = UserDropdown;
const DefaultAlertDropdown = AlertsDropdown;

const cleanCssProperty = (property: string) => property.replace(/--fb-|--bs-|--/, '');

const cleanCssPropertyValue = (value: string) => value.trim();

const useRootBridgeProperties = () => {
  const computedStyle = getComputedStyle(document.documentElement);
  const properties = computedStyle.getPropertyValue('--fb-bridge-properties').trim().split(' ');

  return properties.reduce((index, property) => {
    const key = camelCase(cleanCssProperty(property));
    const value = cleanCssPropertyValue(computedStyle.getPropertyValue(property));
    return {
      ...index,
      [key]: value
    };
  }, {});
};

export type ContextType = {
  AppLogo?: ComponentType<any>;
  appName?: string;
  Loader: LoaderComponentType;
  ImageLoader?: ComponentType<any> | typeof DefaultImageLoader;
  UserDropDown?: ComponentType<any>;
  AlertDropdown?: ComponentType<any>;
  navbarConfig?: any[];
  navbarI18nNamespace?: string;
  children?: React.ReactNode;
};

const CssContext = createContext<ContextType>({
  Loader: DefaultLoader,
});

export function DesignContextProvider({
  AppLogo,
  appName,
  Loader,
  ImageLoader,
  UserDropDown,
  AlertDropdown,
  navbarConfig,
  navbarI18nNamespace = 'translation',
  children
}: ContextType) {
  const rootBridgeProperties = useRootBridgeProperties();
  const value = useMemo(
    () => ({
      ...rootBridgeProperties,
      AppLogo: AppLogo || undefined,
      appName: appName || undefined,
      Loader: Loader || DefaultLoader,
      ImageLoader: ImageLoader || DefaultImageLoader,
      UserDropDown: UserDropDown || DefaultUserDropdown,
      AlertDropdown: AlertDropdown || DefaultAlertDropdown,
      navbarConfig: navbarConfig || undefined,
      navbarI18nNamespace
    }),
    [
      rootBridgeProperties,
      AppLogo,
      appName,
      Loader,
      ImageLoader,
      UserDropDown,
      AlertDropdown,
      navbarConfig,
      navbarI18nNamespace
    ]
  );
  return (
    <CssContext.Provider value={value}>
      {children}
      <Toaster />
    </CssContext.Provider>
  );
}

export const useDesignContext = () => useContext(CssContext);

export default DesignContextProvider;
